/**
 * Profit & Dividends endpoints (bos-api, BOS-112).
 *
 * Paths are relative to the axios client's base, which already ends in
 * `/api/v1` — so `/ledger/dividends/shareholders` reaches
 * `…/api/v1/ledger/dividends/shareholders`. Same shape as
 * `SalariesEndpoints` and `FixedAssetsEndpoints`.
 *
 * **Every route is `OrgAdmin`, reads included.** Distributable profit and the
 * shareholder register are not branch-level facts.
 *
 * A declaration is addressed by its **journal entry id** — a declaration has no
 * identity apart from the entry it created.
 */

/** Ids are server-shaped, but escaping keeps the path well-formed. */
const seg = (value: string): string => encodeURIComponent(value);

export type ShareholderListQueryParams = {
    /** ISO date, `YYYY-MM-DD`. Which rate set resolves `effectiveWithholdingTaxBps`. */
    asOf?: string;
    /** Free text over the localized names **and** the identifier. */
    search?: string;
};

const query = ({ asOf, search }: ShareholderListQueryParams): string => {
    const params = new URLSearchParams();
    if (asOf) {
        params.set('asOf', asOf);
    }
    // A blank search is omitted rather than sent empty: the two mean the same
    // thing to the server, and leaving it out keeps the URL (and the query key
    // it mirrors) readable.
    if (search && search.trim() !== '') {
        params.set('search', search.trim());
    }
    const search_ = params.toString();
    return search_ ? `?${search_}` : '';
};

export const DividendsEndpoints = {
    /** GET — the shareholder register, with the distributable-profit summary. */
    shareholders: (options: ShareholderListQueryParams = {}) =>
        `/ledger/dividends/shareholders${query(options)}`,

    /** POST — create a shareholder. Idempotent on the client id; 201 then 200. */
    createShareholder: () => '/ledger/dividends/shareholders',

    /** PUT — replace every field but the id. */
    updateShareholder: (shareholderId: string) =>
        `/ledger/dividends/shareholders/${seg(shareholderId)}`,

    /**
     * DELETE — remove a shareholder no declaration has ever paid. `204`, or
     * `400 SHAREHOLDER_HAS_DECLARATIONS`. Same rule as the employee register's
     * delete: remove only what nothing hangs off.
     */
    deleteShareholder: (shareholderId: string) =>
        `/ledger/dividends/shareholders/${seg(shareholderId)}`,

    /** POST — the two-way extraction calculator. Pure; writes nothing. */
    calculate: (asOf?: string) =>
        `/ledger/dividends/calculate${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`,

    /** GET — committed declarations, newest first. */
    declarations: () => '/ledger/dividends/declarations',

    /** GET — one declaration's stored breakdown, by **journal entry id**. */
    declaration: (journalEntryId: string) =>
        `/ledger/dividends/declarations/${seg(journalEntryId)}`,

    /** POST — per-shareholder split + the draft a commit would build. Writes nothing. */
    previewDeclaration: () => '/ledger/dividends/declarations/preview',

    /** POST — create the declaration's DRAFT entry and record it. Posts nothing. */
    commitDeclaration: () => '/ledger/dividends/declarations/commit',
};
